import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { InputFieldComponent } from '../../../../components/form/input-field/input-field.component';
import { TextAreaFieldComponent } from '../../../../components/form/textarea-field/textarea-field.component';
import { LoaderComponent } from '../../../../components/widgets/loader/loader.component';
import { LoaderConfig } from '../../../../components/widgets/loader/loader.config';

import { QualityManagementService } from '../../../../services/quality-management.service';
import { LoaderService } from '../../../../services/loader.service';
import { AlertNotificationService } from '../../../../services/alert-notification.service';
import { MainLayoutService } from '../../../../services/main-layout.service';
import { AccessControlService } from '../../../../services/access-control.service';

import { Planning, PlanningPayload, planningUserFullName } from '../../../../models/entities/planning';
import { ActivitiesPlanningHistoryComponent } from '../../components/activities-planning-history/activities-planning-history.component';
import { ActivitiesPlanningPostponeComponent } from '../../components/activities-planning-postpone/activities-planning-postpone.component';

@Component({
  selector: 'app-activities-planning-detail-page',
  imports: [
    ReactiveFormsModule,
    InputFieldComponent,
    TextAreaFieldComponent,
    LoaderComponent,
    ActivitiesPlanningPostponeComponent,
    ActivitiesPlanningHistoryComponent
  ],
  templateUrl: './activities-planning-detail-page.component.html',
  styleUrl: './activities-planning-detail-page.component.scss',
})
export class ActivitiesPlanningDetailPageComponent implements OnInit, OnDestroy {

  @ViewChild(ActivitiesPlanningPostponeComponent) postponePanel?: ActivitiesPlanningPostponeComponent;
  @ViewChild(ActivitiesPlanningHistoryComponent) historyComponent?: ActivitiesPlanningHistoryComponent;

  planningId?: number;
  planning?: Planning;

  form: FormGroup;
  currentUser: any = null;
  canModify: boolean = false;

  isLoading: boolean = false;
  loaderConfig: LoaderConfig = {
    type: 'spinner',
    text: 'Saving...',
    overlay: true
  };

  private readonly LOADER_CONTEXT = 'activities-planning-detail-page';
  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private qualityManagementService: QualityManagementService,
    private loaderService: LoaderService,
    private alertNotificationService: AlertNotificationService,
    private mainLayoutService: MainLayoutService,
    private accessControlService: AccessControlService
  ) {
    this.form = this.formBuilder.group({
      title: [{ value: '', disabled: true }],
      description: [{ value: '', disabled: true }],
      assignee: [{ value: '', disabled: true }],
      reportedBy: [{ value: '', disabled: true }],
      dueDate: [{ value: '', disabled: true }],
      reminder: [{ value: '', disabled: true }]
    }, { validators: qualityManagementService.dueDateAfterReminderValidator('dueDate', 'reminder') });
  }

  ngOnInit(): void {
    this.mainLayoutService.user$
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.currentUser = user;
        this.applyFieldPermissions();
      });

    this.mainLayoutService.activeProfile$
      .pipe(takeUntil(this.destroy$))
      .subscribe(profileCode => {
        this.canModify = this.accessControlService.hasFeatureActionAccess(
          profileCode,
          'QUALITY_MANAGEMENT_ACTIVITIES_PLANNING',
          'modify'
        );
        this.applyFieldPermissions();
      });

    this.loaderService.getLoadingAsObservable(this.LOADER_CONTEXT)
      .pipe(takeUntil(this.destroy$))
      .subscribe(isLoading => this.isLoading = isLoading);

    const paramId = this.route.snapshot.paramMap.get('planningId');
    if (paramId) {
      this.planningId = Number(paramId);
      this.loadPlanning(this.planningId);
    }
  }

  ngOnDestroy(): void {
    this.loaderService.stopLoading(this.LOADER_CONTEXT);
    this.destroy$.next();
    this.destroy$.complete();
  }


  get isCompleted(): boolean {
    return this.qualityManagementService.isPlanningCompleted(this.planning);
  }

  get isCreator(): boolean {
    return this.currentUser?.userId != null
      && Number(this.planning?.creatorUser?.userId) === Number(this.currentUser.userId);
  }

  get canEditReminder(): boolean {
    return this.canModify && this.isCreator && !this.isCompleted;
  }

  get code(): string {
    return this.planning?.code ?? '';
  }

  getControl(name: string): FormControl {
    return this.form.get(name) as FormControl;
  }

  hasDueDateError(): boolean {
    const control = this.form.get('dueDate');
    return !!control?.errors?.['backendError'];
  }

  dueDateErrorMessage(): string {
    return this.form.get('dueDate')?.errors?.['backendError'] ?? '';
  }


  private loadPlanning(planningId: number): void {
    this.loaderService.startLoading(this.LOADER_CONTEXT);

    this.qualityManagementService.detailPlanning(planningId).subscribe({
      next: (response: any) => {
        this.loaderService.stopLoading(this.LOADER_CONTEXT);

        const planning: Planning | null = this.qualityManagementService.readResponseKey(response, 'activityPlanning') ?? null;
        if (!planning) {
          this.alertNotificationService.error('Planning not found.');
          return;
        }

        this.applyPlanning(planning);
      },
      error: (error: any) => {
        console.error('Error loading planning:', error);
        this.loaderService.stopLoading(this.LOADER_CONTEXT);
        this.alertNotificationService.error('Failed to load the planning.');
      }
    });
  }

  private applyPlanning(planning: Planning): void {
    this.planning = planning;

    this.form.patchValue({
      title: planning.title ?? '',
      description: planning.description ?? '',
      assignee: planningUserFullName(planning.assigneeUser) || '-',
      reportedBy: planningUserFullName(planning.creatorUser) || '-',
      dueDate: this.qualityManagementService.toDateTimeLocal(planning.dueDate),
      reminder: this.qualityManagementService.toDateTimeLocal(planning.reminder)
    }, { emitEvent: false });

    this.applyFieldPermissions();
  }

  private applyFieldPermissions(): void {
    const reminderControl = this.form.get('reminder');

    if (this.canEditReminder) {
      reminderControl?.enable({ emitEvent: false });
    } else {
      reminderControl?.disable({ emitEvent: false });
    }

    if (this.isCompleted) {
      this.form.get('dueDate')?.disable({ emitEvent: false });
    }
  }

  onReminderChange(): void {
    if (!this.planningId || !this.canEditReminder) {
      return;
    }

    if (this.form.errors?.['dueDateBeforeReminder']) {
      this.alertNotificationService.error("Due Date can't be earlier than the Reminder.");
      return;
    }

    this.handleSave(
      this.qualityManagementService.updatePlanningReminder(
        this.planningId,
        this.qualityManagementService.fromDateTimeLocal(this.form.get('reminder')?.value)
      ),
      'Reminder updated.'
    );
  }

  onPostponePanelToggle(isOpen: boolean): void {
    const dueDateControl = this.form.get('dueDate');

    if (isOpen && !this.isCompleted && this.canModify) {
      dueDateControl?.enable({ emitEvent: false });
    } else {
      dueDateControl?.disable({ emitEvent: false });
      dueDateControl?.setValue(this.qualityManagementService.toDateTimeLocal(this.planning?.dueDate), { emitEvent: false });
    }
  }

  onPostpone(reason: string): void {
    if (!this.planningId || !this.canModify) {
      return;
    }

    if (this.form.errors?.['dueDateBeforeReminder']) {
      this.alertNotificationService.error("Due Date can't be earlier than the Reminder.");
      return;
    }

    this.handleSave(
      this.qualityManagementService.postponePlanning(this.planningId, this.buildPostponePayload(), reason),
      'Planning postponed.'
    );

    this.postponePanel?.closePanel();
  }

  markAsDone(): void {
    if (!this.planningId || this.isCompleted || !this.canModify) {
      return;
    }

    this.handleSave(
      this.qualityManagementService.changePlanningStatus(this.planningId, 'COMPLETED'),
      'Planning marked as done.'
    );
  }

  private buildPostponePayload(): PlanningPayload {
    return {
      title: this.planning?.title ?? '',
      description: this.planning?.description ?? '',
      assigneeUserId: Number(this.planning?.assigneeUser?.userId),
      dueDate: this.qualityManagementService.fromDateTimeLocal(this.form.getRawValue().dueDate) ?? ''
    };
  }

  private handleSave(request$: Observable<any>, successMessage: string): void {
    if (!this.planningId) {
      return;
    }

    this.loaderService.startLoading(this.LOADER_CONTEXT);

    request$.subscribe({
      next: (response: any) => {
        this.loaderService.stopLoading(this.LOADER_CONTEXT);

        const planning: Planning | null = this.qualityManagementService.readResponseKey(response, 'activityPlanning') ?? null;

        if (planning) {
          this.applyPlanning(planning);
        } else {
          this.loadPlanning(this.planningId!);
        }

        this.historyComponent?.reload();
        this.alertNotificationService.success(successMessage);
      },
      error: (error: any) => {
        console.error('Error saving planning:', error);
        this.loaderService.stopLoading(this.LOADER_CONTEXT);
        this.alertNotificationService.error('Failed to save the planning.');
      }
    });
  }
}
